// Order event log for Order detail V3 — one streamlined row per event
// (name · summary · who · when) that expands to the full detail, with an
// All / Operations Hub / Shopify filter. Same three feeds as the V2 log:
// Shopify timeline events, Operations Hub portal sessions, and the comment thread.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CommentComposer from '../comments/CommentComposer';
import CommentEntry from '../comments/CommentEntry';
import { tokenFor } from '../comments/mentions';
import { renderShopifyMessage, verbLabel, CANCEL_REASON_LABELS } from '../OrderDetail';
import { buildSessions, bundleChildSet, isBundleChildEvent } from '../OrderTimeline';
import { EventDetailBody, sessionMark, withPickupInfo } from './VisualTimelineV3';

const FILTERS = [['all', 'All'], ['ops', 'Operations Hub'], ['shopify', 'Shopify']];
const PAGE = 25;
const SESSION_NAME = { pick: 'Picked', press: 'Pressed', pack: 'Packed', print: 'Printed', other: 'Activity' };
const SESSION_VERB = { pick: 'picked', press: 'pressed', pack: 'passed QC', print: '', other: '' };

const Caret = () => <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M7 4.5 12.5 10 7 15.5z"/></svg>;
const OpsIco = () => <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M3 5.5A1.5 1.5 0 0 1 4.5 4h11A1.5 1.5 0 0 1 17 5.5v2A1.5 1.5 0 0 1 16 8.9V14.5A1.5 1.5 0 0 1 14.5 16h-9A1.5 1.5 0 0 1 4 14.5V8.9A1.5 1.5 0 0 1 3 7.5zM5.5 9.5v5h9v-5zM8 11h4v1.5H8z"/></svg>;
const ShopIco = () => <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M3.5 6 5 3h10l1.5 3v1a2 2 0 0 1-1.5 1.9V16H5V8.9A2 2 0 0 1 3.5 7zM6.5 9v5.5h7V9a3 3 0 0 1-3.5-.7A3 3 0 0 1 6.5 9z"/></svg>;

const fmtWhen = (ms) => {
    const d = new Date(ms);
    const yr = d.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' };
    return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...yr })}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
};
const fmtFull = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const plain = (html) => {
    if (!html) return '';
    try { return (new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html').body.textContent || '').trim(); }
    catch { return String(html); }
};
const baseTitle = (t) => String(t || '').split(' — ')[0];

function ShopifyDetail({ ev, attachments, cancelReason }) {
    const [lightbox, setLightbox] = useState(null);
    const args = Array.isArray(ev.arguments) ? ev.arguments.find(a => a && typeof a === 'object') : null;
    const items = args?.list_item_content || [];
    const hasLoc = !!(args && (args.origin_location_name || args.destination_location_name));
    return (
        <>
            <div className="odv3-ev-meta">
                {ev.author && <><span>By</span><b>{ev.author}</b></>}
                <span>When</span><b>{fmtFull(ev.created_at)}</b>
                {ev.verb === 'cancelled' && cancelReason && (
                    <><span>Reason</span><b>{CANCEL_REASON_LABELS[cancelReason] || String(cancelReason).replace(/_/g, ' ').toLowerCase()}</b></>
                )}
            </div>
            {ev.message && ev.message !== verbLabel(ev.verb) && <div className="odv3-ev-msg">{renderShopifyMessage(ev.message)}</div>}
            {hasLoc && (
                <div className="odv3-ev-msg">
                    Fulfillment location changed from <b>{args.origin_location_name || '—'}</b> to <b>{args.destination_location_name || '—'}</b>
                </div>
            )}
            {items.length > 0 && (
                <div className="odv3-ev-rows">
                    {items.map((it, i) => (
                        <div key={i} className="odv3-ev-row">
                            <div>
                                <div className="odv3-ev-t">{it.product_title || it.sku || 'Item'}</div>
                                {it.sku && it.product_title && <code className="odv3-sku">{it.sku}</code>}
                            </div>
                            <span className="odv3-ev-n">×{it.quantity || 1}</span>
                        </div>
                    ))}
                </div>
            )}
            {attachments?.length > 0 && (
                <div className="odv3-ev-att">
                    {attachments.map((att, i) => (
                        <button key={att.id || i} type="button" onClick={() => setLightbox(att.full_url || att.url)} title="View full size">
                            <img src={att.url} alt="" loading="lazy"/>
                        </button>
                    ))}
                </div>
            )}
            {lightbox && (
                <div className="odv3-ev-scrim odv3-ev-scrim--img" onClick={() => setLightbox(null)}>
                    <img src={lightbox} alt=""/>
                </div>
            )}
        </>
    );
}

export default function EventLogV3({ orderId, order, cancelReason, fetchFn, pickupInfo }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');
    const [openKeys, setOpenKeys] = useState(() => new Set());
    const [showAll, setShowAll] = useState(false);
    const [thread, setThread] = useState({ comments: [], can_post: false, mirror_as: null, me: null });
    const [editing, setEditing] = useState(null);
    const [replyTo, setReplyTo] = useState(null);
    const [actionError, setActionError] = useState(null);
    const hash = useLocation().hash;
    const loadedOnce = useRef(false);

    const location = useMemo(() => {
        const names = [...new Set((order?.fulfillmentOrders?.edges || []).map(e => e?.node?.assignedLocation?.name).filter(Boolean))];
        return names.length === 1 ? names[0] : '';
    }, [order]);

    const load = useCallback(async () => {
        if (!loadedOnce.current) setLoading(true);
        setError(null);
        try {
            const [shopifyRes, portalRes, attachRes, threadRes] = await Promise.all([
                fetchFn(`/api/orders/${orderId}/events`),
                fetchFn(`/api/orders/${orderId}/timeline`),
                fetchFn(`/api/orders/${orderId}/timeline-attachments`),
                fetchFn(`/api/orders/${orderId}/comments`).catch(() => null),
            ]);
            const threadData = (threadRes && threadRes.ok) ? await threadRes.json().catch(() => null) : null;
            const threadSafe = threadData || { comments: [], can_post: false, mirror_as: null, me: null };
            setThread(threadSafe);
            if (!shopifyRes.ok) throw new Error('Failed to load events');
            const shopifyData = await shopifyRes.json();
            const portalData = portalRes.ok ? await portalRes.json() : { events: [], items: {} };
            const attachData = attachRes.ok ? await attachRes.json() : { attachments_by_event: {} };
            const attByEvent = attachData.attachments_by_event || {};
            const itemsMap = portalData.items || {};

            // Shopify comments already mirrored into the OpsHub thread show once (as the thread comment).
            const threadEventIds = new Set((threadSafe.comments || []).map(c => c.shopify_event_id).filter(Boolean).map(String));
            const shopifyEntries = (shopifyData.events || [])
                .filter(ev => !(ev.verb === 'comment' && threadEventIds.has(String(ev.id))))
                .map(ev => ({
                    key: `sh-${ev.id}`, kind: 'shopify', src: 'shopify', ts: new Date(ev.created_at).getTime(),
                    ev, attachments: attByEvent[String(ev.id)] || null,
                }));
            const commentEntries = (threadSafe.comments || []).map(c => ({
                key: `c-${c.id}`, kind: 'comment', src: 'ops', ts: new Date(c.created_at).getTime(), c,
            }));
            const bases = bundleChildSet(portalData.bundle_membership || {});
            const events = (portalData.events || []).filter(e => !isBundleChildEvent(e, bases, itemsMap));
            const sessionEntries = buildSessions(events).map((s, i) => {
                const m = sessionMark(s, i, { itemsMap, events, location });
                return m ? { key: `se-${i}-${s.startTs}`, kind: 'session', src: 'ops', ts: s.endTs, m } : null;
            }).filter(Boolean);

            setEntries([...shopifyEntries, ...sessionEntries, ...commentEntries].sort((a, b) => b.ts - a.ts));
            loadedOnce.current = true;
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    }, [orderId, location]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadedOnce.current = false; load(); }, [load]);

    // Mark mentions read once the loaded thread mentions the viewer (same as V2).
    useEffect(() => {
        if (loading) return;
        const meId = thread.me && thread.me.user_id;
        const mentionsMe = meId != null && (thread.comments || []).some(c =>
            Object.keys(c.mentions || {}).includes(String(meId)) ||
            new RegExp(`\\[@u${meId}\\|`).test(c.body || ''));
        if (mentionsMe) fetchFn(`/api/orders/${orderId}/mentions/seen`, { method: 'POST' }).catch(() => {});
    }, [loading, thread]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (key) => setOpenKeys(prev => {
        const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
    });

    const visible = entries.filter(e => filter === 'all' || e.src === filter);
    const shown = showAll ? visible : visible.slice(0, PAGE);

    const focusComposer = () => setTimeout(() => {
        const t = document.querySelector('.cm-composer .cm-editor');
        if (t) { t.scrollIntoView({ block: 'center' }); t.focus(); }
    }, 0);

    const composer = thread.can_post ? (
        <div className="odv3-log-composer" hidden={Boolean(editing)}>
            <CommentComposer orderId={orderId} storeFetch={fetchFn} mirrorAs={thread.mirror_as}
                prefill={replyTo ? { token: tokenFor(replyTo), nonce: replyTo.nonce } : null}
                onCancel={replyTo ? () => setReplyTo(null) : undefined}
                onPosted={() => { setReplyTo(null); load(); setTimeout(() => load(), 2500); }}/>
        </div>
    ) : null;

    if (loading) return <div className="odv3-stub">Loading events…</div>;
    if (error) return <div className="odv3-stub">{error}</div>;

    return (
        <div className="odv3-elog">
            <div className="odv3-elog-bar">
                <div className="odv3-seg-grp" role="tablist" aria-label="Event source">
                    {FILTERS.map(([id, label]) => (
                        <button key={id} type="button" role="tab" aria-selected={filter === id}
                                className={`odv3-seg-btn${filter === id ? ' odv3-seg-btn--on' : ''}`}
                                onClick={() => setFilter(id)}>{label}</button>
                    ))}
                </div>
            </div>
            {actionError && <div className="odv3-elog-err" role="alert">{actionError}</div>}
            {composer}
            {shown.length === 0 && <div className="odv3-stub">No events{filter !== 'all' ? ' from this source' : ' yet'}.</div>}
            <ul className="odv3-elog-list">
                {shown.map(en => {
                    if (en.kind === 'comment') {
                        const c = en.c;
                        if (editing && editing.id === c.id) {
                            return (
                                <li key={en.key} className="odv3-elog-raw">
                                    <CommentComposer orderId={orderId} storeFetch={fetchFn} mirrorAs={thread.mirror_as}
                                        commentId={c.id} initial={{ body: c.body, images: c.images }}
                                        onCancel={() => setEditing(null)}
                                        onPosted={() => { setEditing(null); load(); setTimeout(() => load(), 2500); }}/>
                                </li>
                            );
                        }
                        return (
                            <li key={en.key} className="odv3-elog-raw">
                                <CommentEntry c={c} highlight={hash === `#comment-${c.id}`}
                                    onReply={thread.can_post ? (x) => {
                                        setReplyTo(x.author_user_id ? { user_id: x.author_user_id, name: x.author_name, mention_id: null, nonce: Date.now() } : null);
                                        focusComposer();
                                    } : undefined}
                                    onEdit={setEditing}
                                    onDelete={async (x) => {
                                        try {
                                            const r = await fetchFn(`/api/comments/${x.id}`, { method: 'DELETE' });
                                            if (!r || !r.ok) throw new Error('Could not delete the comment');
                                            setActionError(null);
                                        } catch (e) { setActionError(e.message || 'Could not delete the comment'); }
                                        load();
                                    }}/>
                            </li>
                        );
                    }
                    const open = openKeys.has(en.key);
                    let name; let msg; let who; let portal = ''; let ico;
                    if (en.kind === 'session') {
                        const m = withPickupInfo(en.m, pickupInfo);
                        name = m.isPickup ? 'Pickup' : m.lane === 'other' && /Auto/.test(m.who) ? 'Auto-completed' : SESSION_NAME[m.lane];
                        const titles = m.rows.map(r => baseTitle(r.title)).filter(Boolean);
                        const uniq = [...new Set(titles)];
                        msg = m.lane === 'print'
                            ? 'Order printed'
                            : m.isPickup ? [[...new Set(m.rows.map(r => r.title).filter(Boolean))].join(', '),
                                m.pk?.total ? `${m.pk.units} of ${m.pk.total} items` : '',
                                m.pk?.names.length ? `Fulfillment ${m.pk.names.join(', ')}` : ''].filter(Boolean).join(' · ')
                            : `${m.items} item${m.items === 1 ? '' : 's'} ${SESSION_VERB[m.lane]}`.trim()
                              + (uniq.length ? ` — ${uniq.slice(0, 2).join(', ')}${uniq.length > 2 ? ` +${uniq.length - 2} more` : ''}` : '');
                        who = m.who; portal = m.portal; ico = <OpsIco/>;
                    } else {
                        const ev = en.ev;
                        name = verbLabel(ev.verb);
                        msg = ev.message && ev.message !== name ? plain(ev.message) : '';
                        who = ev.author || 'Shopify'; ico = <ShopIco/>;
                    }
                    return (
                        <li key={en.key} className="odv3-elog-item">
                            <button type="button" className="odv3-elog-main" aria-expanded={open} onClick={() => toggle(en.key)}>
                                <span className={`odv3-elog-car${open ? ' odv3-elog-car--open' : ''}`}><Caret/></span>
                                <span className={`odv3-elog-src${en.src === 'ops' ? ' odv3-elog-src--ops' : ''}`}>{ico}</span>
                                <span className="odv3-elog-txt">
                                    <span className="odv3-elog-n">{name}</span>
                                    {msg && <span className="odv3-elog-m">{msg}</span>}
                                    <span className="odv3-elog-w">{[who, portal, fmtWhen(en.ts)].filter(Boolean).join(' · ')}</span>
                                </span>
                            </button>
                            {open && (
                                <div className="odv3-elog-detail">
                                    {en.kind === 'session'
                                        ? <EventDetailBody ev={withPickupInfo(en.m, pickupInfo)}/>
                                        : <ShopifyDetail ev={en.ev} attachments={en.attachments} cancelReason={cancelReason}/>}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
            {!showAll && visible.length > PAGE && (
                <div className="odv3-elog-more">
                    <button type="button" className="odv3-btn odv3-btn--slim" onClick={() => setShowAll(true)}>
                        Show all {visible.length} events
                    </button>
                </div>
            )}
        </div>
    );
}
